import instance from '../api/api';

const CACHE_KEY = 'risk_tenant_config_v2';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// Admins can edit perspectives/objectives in Settings, so re-check soon after
// load; the fresh copy is used on the next page load.
const BACKGROUND_REFRESH_MS = 5 * 60 * 1000;

// Always matches whatever server api.js is pointed at — no separate env var needed.
const API_BASE = (instance.defaults.baseURL || 'https://dev.risk-api.africacodefoundry.com').replace(/\/$/, '');

// Fallback map used when the backend API is unreachable.
// Maps known domains to their static config filename in src/configs/.
// Add new entries here whenever a new tenant config file is created,
// until the backend fully serves configs via GET /api/tenant/config.
const STATIC_CONFIG_MAP = {
  'sigstatus.com':                      'sigstatus',
  'rsmbotswana.com':                    'rsmbotswana',
  'rsm-risk.africacodefoundry.com':     'rsmbotswana',
  'calm-field-0f6e96810.6.azurestaticapps.net': 'rsmbotswana',
  'tats.com': 'tats',
  'zealous-stone-09c013b10.7.azurestaticapps.net': 'ngip',
  'boitekanelo-risk.africacodefoundry.com': 'boitekanelo',
};

/**
 * Load tenant config for the current domain.
 *
 * Flow:
 *  1. Return cached config immediately if it's < 24h old
 *  2. If cache is > 5 min old, also silently refresh it in the background
 *  3. If cache is missing or expired, fetch from the backend API
 *  4. If the API is unreachable, fall back to the matching static JSON file in src/configs/
 *  5. If no static file exists for the domain, fall back to default.json
 */
export async function loadTenantConfig(domain) {
  const cleanDomain = domain.split(':')[0];

  const isLocal = cleanDomain === 'localhost' || cleanDomain === '127.0.0.1';
  const effectiveDomain = isLocal && process.env.REACT_APP_DEV_TENANT_DOMAIN
    ? process.env.REACT_APP_DEV_TENANT_DOMAIN
    : cleanDomain;

  if (isLocal && process.env.REACT_APP_DEV_TENANT_DOMAIN) {
    console.log(`[TenantConfig] Dev override: localhost → ${effectiveDomain}`);
  }

  const cached = readCache(effectiveDomain);
  if (cached) {
    console.log('[TenantConfig] Serving from cache');
    maybeRefreshInBackground(effectiveDomain);
    return cached;
  }

  return fetchFromAPI(effectiveDomain);
}

// ─── Cache helpers ────────────────────────────────────────────────────────────

function readCache(domain) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { domain: cachedDomain, config, timestamp } = JSON.parse(raw);
    if (cachedDomain !== domain) return null;
    if (Date.now() - timestamp > CACHE_TTL_MS) return null;
    return config;
  } catch {
    return null;
  }
}

function writeCache(domain, config) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ domain, config, timestamp: Date.now() }));
  } catch {
    // localStorage may be full or unavailable — not fatal
  }
}

function maybeRefreshInBackground(domain) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const { timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > BACKGROUND_REFRESH_MS) {
      console.log('[TenantConfig] Cache approaching expiry — refreshing in background');
      fetchFromAPI(domain).catch(() => {});
    }
  } catch {}
}

// ─── API fetch ────────────────────────────────────────────────────────────────

async function fetchFromAPI(domain) {
  try {
    const response = await fetch(`${API_BASE}/api/tenant/config`, {
      headers: { 'X-Tenant-Domain': domain },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const json = await response.json();
    const config = json.data ?? json;

    writeCache(domain, config);
    console.log('[TenantConfig] Loaded from API and cached for', domain);
    return config;
  } catch (error) {
    console.warn('[TenantConfig] API fetch failed, trying static fallback:', error.message);
    return loadStaticConfig(domain);
  }
}

async function loadStaticConfig(domain) {
  const configName = STATIC_CONFIG_MAP[domain];
  if (configName) {
    try {
      const config = await import(`../configs/${configName}.json`);
      console.warn(`[TenantConfig] Serving static fallback: ${configName}.json`);
      return config.default ?? config;
    } catch {}
  }
  return loadDefaultConfig();
}

async function loadDefaultConfig() {
  try {
    const config = await import('../configs/default.json');
    console.warn('[TenantConfig] Serving default.json fallback');
    return config.default ?? config;
  } catch (err) {
    console.error('[TenantConfig] Could not load default config:', err);
    return null;
  }
}

// ─── Public helpers ───────────────────────────────────────────────────────────

export function getCurrentDomain() {
  return window.location.hostname;
}

/**
 * Force a fresh fetch on the next page load.
 * Call this if you know the tenant config has been updated.
 */
export function clearTenantCache() {
  localStorage.removeItem(CACHE_KEY);
  console.log('[TenantConfig] Cache cleared');
}
